import { zodResolver } from "@hookform/resolvers/zod";
import { type FleetHostCreateInput, FleetHostCreateSchema } from "@schemas";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import type { WorkerRow } from "@/lib/workers/types";
import { detectedLocationLabel } from "@/pages/cluster/geo-routing";
import {
	NEW_WORKER_DEFAULTS,
	toWorkerPayload,
	WorkerConfigFields,
} from "./worker-config-fields";

/**
 * Add/edit worker dialog. The parent component is expected to set a stable
 * `key` (e.g. `editing?.hostname ?? "new"`) so React remounts the dialog when
 * the target row changes - that gives us fresh defaultValues without needing a
 * `useEffect` to manually reset the form.
 */
export function HostFormDialog({
	open,
	onOpenChange,
	host,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	host: WorkerRow | null;
}) {
	const queryClient = useQueryClient();
	const isEdit = host !== null;
	// A worker cluster-manager deployed owns its own name: it is the container
	// name and the VEIL_WORKER_ID the worker routes its browser URLs with, both
	// fixed at create. The API refuses the rename; the field says so first.
	const nameIsFixed = isEdit && host.managed;

	const form = useForm<FleetHostCreateInput>({
		resolver: zodResolver(FleetHostCreateSchema),
		defaultValues: host
			? {
					hostname: host.hostname,
					publicIp: host.publicIp,
					lbPort: host.lbPort,
					weight: host.weight,
					enabled: host.enabled,
					region: host.region,
					apiKey: host.apiKey ?? "",
					statusEndpoint: host.statusEndpoint,
					basePath: host.basePath,
					probePort: host.probePort,
					placement: host.placement,
					notes: host.notes ?? "",
				}
			: NEW_WORKER_DEFAULTS,
	});

	const save = useMutation({
		mutationFn: async (values: FleetHostCreateInput) => {
			const body = toWorkerPayload(values);
			if (isEdit && host) {
				return unwrap(api.api.workers({ hostname: host.hostname }).patch(body));
			}
			return unwrap(api.api.workers.post(body));
		},
		onSuccess: (_data, values) => {
			toast.success(
				isEdit ? `Updated ${values.hostname}` : `Added ${values.hostname}`,
			);
			queryClient.invalidateQueries({ queryKey: ["cluster"] });
			queryClient.invalidateQueries({ queryKey: ["workers"] });
			onOpenChange(false);
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Save failed"),
	});

	const onSubmit = form.handleSubmit((values) => save.mutate(values));
	const submitting = save.isPending;

	return (
		<ResponsiveDialog open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent className="flex max-h-[min(92vh,44rem)] w-[min(100%-1.5rem,42rem)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
				<ResponsiveDialogHeader className="shrink-0 space-y-0 border-b border-border/60 px-6 pt-6 pr-12 pb-4 md:px-8 md:pr-14">
					<ResponsiveDialogTitle className="text-lg font-semibold tracking-tight">
						{isEdit ? `Edit ${host?.hostname}` : "Register a worker"}
					</ResponsiveDialogTitle>
					<ResponsiveDialogDescription className="pt-2 text-pretty leading-relaxed">
						{isEdit
							? "Changes save immediately to the database. Hit Render & Reload to push them to nginx."
							: "Records a worker that is already running elsewhere. Render & Reload after to make it live."}
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>
				<form
					onSubmit={onSubmit}
					noValidate
					className="flex min-h-0 flex-1 flex-col"
				>
					<div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-6 md:px-8">
						<WorkerConfigFields
							form={form}
							nameIsFixed={nameIsFixed}
							// Only remote workers need this: same-box workers are rolled
							// directly over the docker socket.
							showAgentFields={!host?.managed}
							hasUpdaterAgent={host?.hasUpdaterAgent ?? false}
							detectedLocation={host ? detectedLocationLabel(host) : undefined}
						/>
					</div>
					<ResponsiveDialogFooter className="mx-0 mb-0 shrink-0 gap-3 border-t border-border/80 bg-muted/50 px-6 py-5 md:px-8 sm:flex-row sm:justify-end">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={submitting}>
							{submitting && (
								<Loader2 data-icon="inline-start" className="animate-spin" />
							)}
							{isEdit ? "Save" : "Register worker"}
						</Button>
					</ResponsiveDialogFooter>
				</form>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
